/**
 * Shared dataviz tokens + geometry helpers for the shared viz layer.
 *
 * Light-mode only (the app theme is `mode: 'light'`), so the dark half of the
 * original reference mockup is dropped entirely — no CSS vars, no theme plumbing,
 * plain constants.
 *
 * PALETTE SOURCE (2026-10-07): the `:root` block of the BP dashboard mock
 * (`recommandations.html`). Adopted verbatim and app-wide — the Conversations
 * dashboard reads the same tokens and follows. Two deliberate departures:
 *  - the FONT stays Inter / Inter Tight (the mock asks for Geist; adopting it is a
 *    product-wide type decision, not a dataviz one);
 *  - PER-ENGINE colours are NOT taken from the mock. It harmonises them
 *    (Perplexity teal, ChatGPT green); the real marks live in `ENGINE_META`, and a
 *    brand logo next to a bar in the wrong brand colour reads as a bug.
 *
 * Rules these tokens encode (dataviz skill):
 *  - `series` is categorical and FIXED-ORDER; never cycled past slot 4.
 *  - `ramp` is a single-hue ordinal scale (5 steps) — use it for part-of-whole /
 *    ordered splits, never as a categorical palette.
 *  - status hues (`good`…`critical`) are never reused as a series colour.
 *  - text never wears the series colour: labels use `ink`/`ink2`/`muted`, except a
 *    label set INSIDE a coloured fill, which goes through `inkOn()`.
 */

import { useEffect, useRef, useState } from 'react';

export const VIZ = {
  /** Categorical, fixed order: violet, green, orange, blue — all from the mock. */
  series: ['#7339E8', '#3F9A6B', '#E0602A', '#4A7BE0'] as const,
  /** Single-hue ordinal, light → dark. The mock's `.iscale` gradient, stepped. */
  ramp: ['#D9CCF8', '#B79CF0', '#9470E9', '#7339E8', '#5B25CC'] as const,
  /** Brand violet — the primary data colour, and the active-state hue. */
  violet: '#7339E8',
  violetDark: '#5B25CC',
  lavender: '#EFE9FC',
  good: '#3F9A6B',
  goodDark: '#2F8A5C',
  /**
   * Amber. The ONE status hue the mock does not define: an ordered 4-step
   * alignment ramp (strong → partial → weak → misaligned) needs a step between
   * its green and its orange, and reusing `neutral` grey there would read as
   * "not measured" rather than as a middling verdict.
   */
  warning: '#D9A13B',
  serious: '#E0602A',
  critical: '#D9483B',
  /**
   * Grey. The sentiment « Neutre » segment and nothing else — a neutral tone is a
   * MEASURED verdict, so it gets an ink-bearing fill, not a status hue.
   */
  neutral: '#E9E7EE',
  deltaUpGood: '#2F8A5C',
  grid: '#F0F0F3',
  /** The baseline gridline + every 1px rule inside a plot. */
  axis: '#E4E4E8',
  ink: '#141418',
  /** Label ink — card titles, table bodies, legend text. */
  ink2: '#3A3A44',
  muted: '#6B6B76',
  surface: '#FFFFFF',
  /** Card hairline. */
  border: '#ECECEF',
  /** The unfilled part of a progress bar. */
  track: '#EFEFF2',
  /** Page background, for a card that must sit INSIDE another card. */
  bg: '#F7F7F8',
  /** Row hover inside a `VizTable`. */
  rowHover: '#FAF8FE',
  /** « n. m. » / « — » cells: present, measured, and empty are three states. */
  nodata: '#A3A3AD',
};

/** Card geometry, shared by `ChartCard`, `StatTile` and the notice panel. */
export const CARD = {
  radius: 20,
  tileRadius: 14,
  noticeRadius: 16,
  padX: 3.5,
  padY: 3,
  gap: 2.5,
};

function mix(hex: string, towards: number, target: number): string {
  const h = hex.replace('#', '');
  const ch = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  const out = ch.map((c) => Math.round(c * (1 - towards) + target * towards));
  return `#${out.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Mix towards WHITE — a pale fill of a series/status colour (chart area, value
 * pill, chip). `amount` is how far TO white, so `tint(c, 0.88)` is a wash.
 * Derived rather than hand-listed: the mock spells out one pair per hue
 * (`#E8F4EE` on `#3F9A6B`, `#FCEDE5` on `#E0602A`) and a hand-list would have to
 * grow a new entry for every engine colour the pills can take.
 */
export function tint(hex: string, amount: number): string {
  return mix(hex, amount, 255);
}

/** Mix towards BLACK — readable ink ON a `tint()` of the same hue. */
export function shade(hex: string, amount: number): string {
  return mix(hex, amount, 0);
}

/** Horizontal bar path: DATA end rounded 4px, square at the baseline. */
export function hBarPath(x: number, y: number, w: number, h: number, r = 4): string {
  const rr = Math.max(0, Math.min(r, w, h / 2));
  if (w <= 0.5) return `M${x},${y}h0`;
  return `M${x},${y}h${w - rr}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - 2 * rr}a${rr},${rr} 0 0 1 ${-rr},${rr}h${-(w - rr)}z`;
}

/** Relative luminance -> ink or white for a label SET INSIDE a coloured fill. */
export function inkOn(hex: string): string {
  const h = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.42 ? VIZ.ink : '#ffffff';
}

/**
 * Ticks from 0 to the first "nice" step at or above `max`.
 * The LAST element is the axis maximum — callers scale against `ticks[ticks.length - 1]`.
 */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0) || !isFinite(max)) return [0];
  const raw = max / Math.max(1, count);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let i = 0; i <= 64; i++) {
    const v = +(i * step).toFixed(6);
    out.push(v);
    if (v >= max - 1e-9) break;
  }
  return out;
}

/**
 * Number locale for every formatter below. Module-level because the formatters are
 * called from INSIDE the primitives (tooltips, tick labels, inline bar labels), where
 * threading it as a prop would mean a `locale` on all six components. The container
 * sets it once from `i18n.language`; French is the default, like the rest of the product.
 */
let vizLocale = 'fr-FR';

export function setVizLocale(lang: string | undefined) {
  vizLocale = lang?.toLowerCase().startsWith('en') ? 'en-US' : 'fr-FR';
}

/** `0.336` -> `33,6 %` (FR) / `33.6%` (EN). */
export function fmtPct(v: number, digits = 1): string {
  const n = (v * 100).toLocaleString(vizLocale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  // The no-break space before the % sign is a French typographic rule, not an English one.
  return vizLocale === 'fr-FR' ? `${n} %` : `${n}%`;
}

/** `12400` -> `12 400` (FR) / `12,400` (EN). */
export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString(vizLocale);
}

/**
 * Signed delta -> arrow + magnitude + colour. Colour NEVER carries the meaning
 * alone: the arrow glyph is part of the returned text, and callers add the unit.
 * `v` is in the caller's own unit (points, counts) — a magnitude under 0.05 reads flat.
 */
export function fmtDelta(v: number, goodUp = true): { text: string; color: string } {
  const flat = !isFinite(v) || Math.abs(v) < 0.05;
  const arrow = flat ? '→' : v > 0 ? '↑' : '↓';
  const good = goodUp ? v > 0 : v < 0;
  const color = flat ? VIZ.muted : good ? VIZ.deltaUpGood : VIZ.critical;
  const mag = Math.abs(v).toLocaleString(vizLocale, { maximumFractionDigits: 1 });
  return { text: `${arrow} ${mag}`, color };
}

export const VIZ_FONT = 'system-ui, -apple-system, "Segoe UI", sans-serif';

let measureCtx: CanvasRenderingContext2D | null | undefined;

/**
 * Real text width, so a value label can be placed outside a bar or moved inside —
 * never clipped, never `overflow: hidden`. Falls back to a per-character estimate.
 */
export function measureText(text: string, fontSize = 11, weight = 400): number {
  if (measureCtx === undefined) {
    measureCtx =
      typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return text.length * fontSize * 0.55;
  measureCtx.font = `${weight} ${fontSize}px ${VIZ_FONT}`;
  return measureCtx.measureText(text).width;
}

/** Container width via ResizeObserver — each chart sizes to its card. */
export function useMeasuredWidth<T extends HTMLElement>(fallback = 640) {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    setWidth(node.clientWidth || fallback);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width] as const;
}
